import { BusinessRole } from '../types/api/user'
import { routes } from './routes'

/**
 * Конфигурация доступа к роутам на основе ролей
 * ЛОГИКА БЕЛОГО СПИСКА: если роут не указан или не имеет requiredRoles, доступ закрыт
 * Только роуты с явно указанными requiredRoles доступны соответствующим ролям
 * SuperAdmin имеет доступ ко всем роутам автоматически
 */
export const routePermissions: Record<
    string,
    {
        requiredRoles?: BusinessRole | BusinessRole[]
        requireAll?: boolean
    }
> = {
    [routes.home]: {
        // Главная страница доступна всем аутентифицированным пользователям
        // Для этого используем специальную логику в isRouteAccessible
    },

    [routes.profile]: {
        // Профиль доступен всем аутентифицированным пользователям
    },

    [routes.calculations]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Manager, BusinessRole.SecondManager],
    },

    [routes.ribbons]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Manager, BusinessRole.SecondManager],
    },

    [routes.orders]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Manager, BusinessRole.SecondManager],
    },

    // Все остальные роуты доступны только SuperAdmin
    [routes.clients]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Manager,
            BusinessRole.SecondManager,
            BusinessRole.Logist,
        ],
    },
    [routes.clientsAdd]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Manager,
            BusinessRole.SecondManager,
            BusinessRole.Logist,
        ],
    },
    [routes.clientsEdit]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Manager,
            BusinessRole.SecondManager,
            BusinessRole.Logist,
        ],
    },

    [routes.equipmentStamps]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.equipmentStamp]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.equipmentStampsLabelPrinting]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.equipmentRollers]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.equipmentFlexoForms]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Storekeeper,
            BusinessRole.StorekeeperForForms,
        ],
    },

    [routes.purchasesStampOrders]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.purchasesStampOrder]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.purchasesMaterialOrders]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.OrdersOperatorForMaterials],
    },
    [routes.purchasesMaterialOrderRequests]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Manager],
    },

    [routes.writeOffsStampWriteOffs]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },

    [routes.receiptsMaterials]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.OrdersOperatorForMaterials,
            BusinessRole.Storekeeper,
            BusinessRole.StorekeeperForMaterial,
        ],
    },
    [routes.receiptsFlexoForms]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.StorekeeperForForms],
    },
    [routes.balancesMaterials]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Manager,
            BusinessRole.PrintTaskManager,
        ],
    },
    [routes.stampMainPage]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.PrintTaskManager, BusinessRole.Curator],
    },

    [routes.shipments]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.Manager,
            BusinessRole.SecondManager,
            BusinessRole.Logist,
        ],
    },
    [routes.shifts]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.PrintTaskManager],
    },

    [routes.schedulesFlexo]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.PrintTaskManager,
            BusinessRole.Manager,
            BusinessRole.SecondManager,
            BusinessRole.Curator,
        ],
    },
    [routes.schedulesFlexoMachines]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.PrintTaskManager,
            BusinessRole.Printer,
        ],
    },
    [routes.schedulesDigital]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.PrintTaskManager,
            BusinessRole.Manager,
            BusinessRole.Curator,
        ],
    },
    [routes.schedulesRewind]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.RewindTaskManager],
    },
    [routes.schedulesRewindMachines]: {
        requiredRoles: [
            BusinessRole.SuperAdmin,
            BusinessRole.RewindTaskManager,
            BusinessRole.Rewinder,
        ],
    },

    [routes.designerTasks]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Designer],
    },
    [routes.designerTasksOvertime]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Designer],
    },
    [routes.curatorTasks]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.Curator],
    },

    [routes.printPreparation]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.StorekeeperForForms],
    },
    [routes.consumption]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.StorekeeperForMaterial],
    },
    [routes.technicalCards]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.printHistory]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.PrintTaskManager],
    },
    [routes.volumeSummary]: {
        requiredRoles: [BusinessRole.SuperAdmin, BusinessRole.PrintTaskManager],
    },
    [routes.rewindHistory]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
    [routes.completedPrintJobs]: {
        requiredRoles: BusinessRole.SuperAdmin,
    },
}

/**
 * Получить требуемые роли для роута
 */
export const getRoutePermissions = (route: string) => {
    return routePermissions[route] || {}
}

/**
 * Проверить, доступен ли роут для пользователя с указанными ролями
 * ЛОГИКА БЕЛОГО СПИСКА: по умолчанию доступ закрыт
 * SuperAdmin имеет доступ ко всем роутам автоматически
 */
export const isRouteAccessible = (route: string, userRoles: BusinessRole[] = []): boolean => {
    // SuperAdmin имеет доступ ко всем роутам
    if (userRoles.includes(BusinessRole.SuperAdmin)) {
        return true
    }

    const permissions = getRoutePermissions(route)

    // Главная страница доступна всем аутентифицированным пользователям
    if (route === routes.home) {
        return true
    }

    // Если для роута не указаны requiredRoles, доступ закрыт (белый список)
    if (!permissions.requiredRoles) {
        return false
    }

    if (userRoles.length === 0) {
        return false // У пользователя нет ролей
    }

    const requiredRoles = Array.isArray(permissions.requiredRoles)
        ? permissions.requiredRoles
        : [permissions.requiredRoles]

    if (permissions.requireAll) {
        // Требуются все роли
        return requiredRoles.every(role => userRoles.includes(role))
    } else {
        // Достаточно одной роли
        return requiredRoles.some(role => userRoles.includes(role))
    }
}
